import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate, Navigate } from 'react-router-dom';
import styled from 'styled-components';
import { Eye, EyeOff } from "lucide-react";

const Page = styled.div`
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f6f7fb;
  padding: 2rem;
`;

const Card = styled.div`
  background: #002e5d;
  padding: 2.5rem 2rem;
  border-radius: 20px;
  width: 100%;
  max-width: 420px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15);
  text-align: center;
  color: white;
`;

const Heading = styled.h2`
  font-size: 1.8rem;
  font-weight: 700;
  margin-bottom: 0.75rem;
`;

const Subheading = styled.p`
  font-size: 0.95rem;
  color: #d9e2ec;
  margin-bottom: 1.5rem;
`;

const Input = styled.input`
  width: 100%;
  padding: 0.75rem;
  margin-bottom: 1rem;
  border-radius: 6px;
  border: none;
  font-size: 1rem;
  box-shadow: inset 0 0 0 1px #ccd;
`;

const Button = styled.button`
  width: 100%;
  padding: 0.75rem;
  background: white;
  color: #002e5d;
  font-weight: 600;
  font-size: 1rem;
  border: none;
  border-radius: 999px;
  cursor: pointer;
  margin-top: 0.5rem;
  transition: background 0.2s;

  &:hover {
    background: #f3f4f6;
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const Error = styled.div`
  color: #ff6b6b;
  font-size: 0.9rem;
  margin-bottom: 1rem;
`;

export default function ResetPassword() {
  const location = useLocation();
  const navigate = useNavigate();

  const [token, setToken] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [validating, setValidating] = useState(true);
  const [tokenValid, setTokenValid] = useState(false);

  // Extract token and validate it
  useEffect(() => {
    const validateToken = async () => {
      const params = new URLSearchParams(location.search);
      const t = params.get('token');
      
      if (!t) {
        navigate('/reset-expired', { replace: true });
        return;
      }

      setToken(t);

    setTokenValid(true);
    setValidating(false);
    };

    validateToken();
  }, [location.search, navigate]);

  // Show loading state while validating token
  if (validating) {
    return (
      <Page>
        <Card>
          <Heading>Validating Reset Link...</Heading>
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '2rem' }}>
            <div style={{
              width: '32px',
              height: '32px',
              border: '3px solid rgba(255,255,255,0.3)',
              borderTop: '3px solid white',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite'
            }}></div>
          </div>
          <style>{`
            @keyframes spin {
              0% { transform: rotate(0deg); }
              100% { transform: rotate(360deg); }
            }
          `}</style>
        </Card>
      </Page>
    );
  }

  // If token is invalid, this shouldn't render (should have redirected)
  if (!tokenValid) {
    return <Navigate to="/reset-expired" replace />;
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');

    try {
      const response = await fetch(
        'https://6l7xr03tz5.execute-api.us-east-1.amazonaws.com/prod/request-reset/reset-password',
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            token: token.trim(),
            password,
          }),
        }
      );

      const data = await response.json();

      if (response.ok) {
        navigate('/?modal=login');
      } else {
        setError(data.message || 'Failed to reset password');
      }
    } catch (err) {
      console.error(err);
      setError('Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Page>
      <Card>
        <Heading>Reset Your Password</Heading>
        <Subheading>
          Let’s reset your Skroop password. Enter your new password.
        </Subheading>

        <form onSubmit={handleSubmit}>
          <div style={{ position: "relative" }}>
            <Input
              type={showPassword ? "text" : "password"}
              name="new-password"
              autoComplete="new-password"
              placeholder="New password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              style={{ paddingRight: "2.5rem" }}
            />
            <span
              onClick={() => setShowPassword(!showPassword)}
              style={{
                position: "absolute",
                right: "0.75rem",
                top: "50%",
                transform: "translateY(-50%)",
                display: "flex",
                alignItems: "center",
                height: "100%",
                cursor: "pointer",
                color: "#6b7280",
              }}
            >
              {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
            </span>
          </div>

          {error && <Error>{error}</Error>}
          <Button type="submit" disabled={submitting}>
            {submitting ? 'Submitting...' : 'Reset Password'}
          </Button>
        </form>
      </Card>
    </Page>
  );
}
